import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import flashMessageMixin from './../mixins/_flash_messages';
import jsonp from 'jsonp';

/**
 * 曜日定義
 *
 * 英字はfull-calendarのclassキー、
 * 数字はmomentの曜日番号
 */
const WEEKS = {
    0: 'sun',
    1: 'mon',
    2: 'tue',
    3: 'wed',
    4: 'thu',
    5: 'fri',
    6: 'sat',
    holiday: 'holiday'
};

// デフォルト指定日タイプの番号
const DEFAULT_DATE_TYPE = 0;

// 祝日定義ファイルのURL(ogariaを参照)
const HOLIDAYS_DATA = 'https://data.ogaria-tool.com/holidays.json';

// 祝日定義ファイルの取得失敗メッセージ
const HOLIDAYS_DATA_GET_ERROR_MESSAGE = 'カレンダーの読み込みに失敗しました。再度画面読み込み直してもエラーが解消されない場合は運営へお問い合わせください。';

/**
 * カレンダーで日ごとの設定を管理するコンポーネント
 */
export default {
    name: 'CalendarController',
    template: '#calendar_controller_template',
    data() {
        return {
            // fullCalendarのオプション
            calendarOptions: {
                plugins: [
                    dayGridPlugin,
                    interactionPlugin
                ],
                initialView: 'dayGridMonth',
                buttonText: {
                    today: '今日'
                },
                locale: 'ja',
                height: 500,
                validRange: {
                    start: moment().startOf('month').format('YYYY-MM-DD'),
                    end: moment().startOf('month').add(12, 'months').format('YYYY-MM-DD')
                },
                // 日付のクリックイベントハンドラ
                dateClick: this.handleDateClick,
                // 「翌月」「先月」ボタンを押した時のCALLBACK
                datesSet: this.viewRender,
                // 「1日」から「日」をなくす
                dayCellContent: function (e) {
                    e.dayNumberText = e.dayNumberText.replace('日', '');
                }
            },
            // 祝日リスト
            holidays: {},
            // 指定日タイプの設定リスト
            date_types: [
                {
                    title: '営業日',
                    color: '#333333',
                    background: '#ffffff',
                },
                {
                    title:'休業日',
                    color:'#ffffff',
                    background:'#F44336',
                }
            ]
        };
    },
    props: {
        // カレンダー設定データ
        modelValue: {
            type: Object,
            require: true
        },
        // 指定日タイプの最大利用数
        max_date_type_count: {
            type: Number,
            required: true
        },
    },
    mixins: [
        flashMessageMixin,
    ],
    components: {
        // カレンダーライブラリ
        FullCalendar
    },
    /**
     * 算出プロパティ
     */
    computed: {
        // valueの変数名を変更
        config_days: {
            get: function () {
                return this.modelValue;
            },
            set: function (value) {
                this.$emit('update:modelValue', value);
            }
        },
        // 指定日タイプの数
        date_types_count: function () {
            return Number(Object.keys(this.date_types).length);
        },
        // 一括指定用ボタンのスタイルデータ
        date_type_style: function () {
            let styles = {};
            if (this.date_types_count > 0) {
                for (let key of Object.keys(WEEKS)) {
                    let week = WEEKS[key];
                    let dateType = this.config_days.regular[key];
                    styles[week] = this.date_types[dateType];
                }
            }
            return styles;
        },
        // 一括指定用チェックボックス版データ
        regular_checks: function () {
            let checks = {};
            for (let key of Object.keys(WEEKS)) {
                const week = WEEKS[key];
                const dateType = this.config_days.regular[key];
                // 営業日以外はチェック(true)
                checks[week] = (dateType !== DEFAULT_DATE_TYPE);
            }
            return checks;
        },
        // 一括指定がされていないか(すべてDEFAULT_DATE_TYPEだったらtrue)
        isRegularAllDefault: function () {
            for (let key of Object.keys(this.config_days.regular)) {
                if (this.config_days.regular[key] !== DEFAULT_DATE_TYPE) {
                    return false;
                }
            }
            return true;
        },
        // 祝日定義が取得されているか
        hasHolidays: function () {
            return (Object.keys(this.holidays).length > 0);
        }
    },
    mounted: function () {
        this.getHolidays();
    },
    watch: {
        holidays: function () {
            this.setCalendarUi();
        }
    },
    emits: ['update:modelValue'],
    methods: {
        /**
         * 一括指定をすべて初期化する
         */
        regularAllDefault: function () {
            for (let key of Object.keys(this.config_days.regular)) {
                this.config_days.regular[key] = DEFAULT_DATE_TYPE;
            }
            // カレンダーUI更新
            this.setCalendarUi();
            this.emitUpdateValue();
        },
        /**
         * 指定曜日のindex番号を取得
         * @param {string} week sun|mon等
         */
        getWeekKey: function (week) {
            return Object.keys(WEEKS).filter((key) => {
                return WEEKS[key] === week;
            })[0];
        },
        /**
         * 指定の日が一括曜日指定、もしくは祝日指定がすでに設定されてる場合、そのdateTypeを返す(祝日優先)
         * @param {date} day 日付YYYY-MM-DD
         */
        alreadySelectedNowDateType: function (day) {
            // 祝日指定のdataType取得
            if (this.config_days.regular.holiday > DEFAULT_DATE_TYPE && typeof this.holidays[day] !== 'undefined') {
                return this.config_days.regular.holiday;
            }
            // 一括曜日指定のdataType取得
            const week = moment(day).day();
            if (this.config_days.regular[week] > DEFAULT_DATE_TYPE) {
                return this.config_days.regular[week];
            }
            return DEFAULT_DATE_TYPE;
        },
        /**
         * 指定の日が一括曜日指定、もしくは祝日指定がすでに設定されているか否か
         * @param {date} day 日付YYYY-MM-DD
         */
        isAlreadySelected: function (day) {
            if (this.alreadySelectedNowDateType(day) > DEFAULT_DATE_TYPE) {
                return true;
            }
            return false;
        },
        /**
         * 個別指定の強制未指定を解除
         * 曜日、祝日指定がすべて解除されている状態の場合実行
         */
        forceUnselectRelease: function () {
            // 個別強制未指定をピックアップ(0指定)
            const temporary = this.config_days.temporary;
            const days = Object.keys(temporary).filter((key) => {
                return temporary[key] === DEFAULT_DATE_TYPE;
            });
            // 対象日が曜日、祝日指定されていなければ配列削除
            for (let day of days) {
                if (!this.isAlreadySelected(day)) {
                    delete this.config_days.temporary[day];
                }
            }
        },
        /**
         * 日付をクリックしたら次の指定日タイプに変更する
         */
        handleDateClick: function (date) {
            const day = date.dateStr;
            const now = moment().format('YYYY-MM-DD');
            // 過去日は操作させない
            if (moment(day).isBefore(now)) {
                return;
            }
            // dateTypeを取得
            // 指定日の送り順調整(曜日指定等を加味する)
            let dateType = this.alreadySelectedNowDateType(day);
            if (typeof this.config_days.temporary[day] !== 'undefined') {
                dateType = Number(this.config_days.temporary[day]);
            }
            // 次のdateTypeへ
            dateType++;
            // dateTypeが無くなったら0にして未指定に
            if (this.date_types_count === dateType) {
                dateType = DEFAULT_DATE_TYPE;
            }
            // dateType更新
            this.config_days.temporary[day] = dateType;
            // 曜日指定等なければ配列を削除(未指定状態にする)
            if (!this.isAlreadySelected(day) && dateType === DEFAULT_DATE_TYPE) {
                delete this.config_days.temporary[day];
            }
            // 日付をキーにするとobjectにならない為、{}で初期化してから入れなおす
            this.config_days.temporary = Object.assign({}, this.config_days.temporary);
            // カレンダーUI更新
            this.setCalendarUi();
            this.emitUpdateValue();
        },
        /**
         * 曜日一括をクリックしたら次の指定日タイプに変更する
         * dateType 0で未指定、index番号指定
         */
        weekClick: function (week) {
            week = this.getWeekKey(week);
            // dateTypeを+1して次の指定にする
            let dateType = Number(this.config_days.regular[week]) + 1;
            // 次の指定日タイプが無くなったら0(未指定状態にする)
            if (this.date_types_count === dateType) {
                dateType = DEFAULT_DATE_TYPE;
            }
            // dateType更新
            this.config_days.regular[week] = dateType;
            // 個別指定の強制未指定を解除
            this.forceUnselectRelease();
            // カレンダーUI更新
            this.setCalendarUi();
            this.emitUpdateValue();
        },
        /**
         * カレンダーが切り替わったらUI更新
         */
        viewRender: function () {
            if (typeof this.config_days !== 'undefined' && Object.keys(this.config_days).length != 0) {
                this.setCalendarUi();
                this.emitUpdateValue();
            }
        },
        /**
         * 設定されている指定日をカレンダーへ反映
         * 個別 ＞ 祝日一括 ＞ 曜日一括 ＞ デフォルト の順で優先度が高い
         */
        setCalendarUi: function () {
            // デフォルトカラーを適応(デフォルトカラーですべて初期化)
            this.cellPaint(
                '', // selecter カレンダーの指定箇所セレクタ(空で全部)
                DEFAULT_DATE_TYPE // dateType 指定日タイプキー
            );
            // 曜日一括
            const regular = this.config_days.regular;
            for (let key of Object.keys(regular)) {
                // カレンダーにカラーをセット(デフォルトカラーの場合は指定しない)
                if (regular[key] !== DEFAULT_DATE_TYPE) {
                    this.cellPaint(
                        '.fc-daygrid-day.fc-day-' + WEEKS[key], // selecter カレンダーの指定箇所セレクタ
                        regular[key] // dateType 指定日タイプキー
                    );
                }
            }
            // 祝日一括
            const holidays = this.holidays;
            for (let key of Object.keys(holidays)) {
                // カレンダーにカラーをセット(デフォルトカラーの場合は指定しない)
                if (this.config_days.regular.holiday !== DEFAULT_DATE_TYPE) {
                    this.cellPaint(
                        '[data-date="' + key + '"]', // selecter カレンダーの指定箇所セレクタ
                        this.config_days.regular.holiday // dateType 指定日タイプキー
                    );
                }
            }
            // 個別
            const temporary = this.config_days.temporary;
            for (let key of Object.keys(temporary)) {
                // カレンダーにカラーをセット(デフォルトカラーの場合も指定)
                this.cellPaint(
                    '[data-date="' + key + '"]', // selecter カレンダーの指定箇所セレクタ
                    temporary[key] // dateType 指定日タイプキー
                );
            }
            // 本日のカラー調整
            $(this.getCalendarElement())
                .find('.fc-day-today .fc-daygrid-day-number')
                .css({
                    'color': $(this.getCalendarElement()).find('.fc-day-today').css('background-color'),
                    'background': $(this.getCalendarElement()).find('.fc-day-today').css('color')
                });
        },
        /**
         * jqueryで直接DOMへカラーstyleを適応する
         * @param {string} selecter 指定のクラス等
         * @param {number} dateType 指定日タイプ(配列キー)
         */
        cellPaint: function (selecter, dateType) {
            // テキストカラー
            $(this.getCalendarElement())
                .find('.fc-day' + selecter).not('.fc-day-disabled')
                .css({
                    'color': this.date_types[dateType].color
                });
            // 背景色
            $(this.getCalendarElement())
                .find('.fc-day' + selecter).not('.fc-day-disabled')
                .css({
                    'background': this.date_types[dateType].background
                });
        },
        /**
         * 祝日定義リストを取得
         */
        getHolidays: function () {
            jsonp(HOLIDAYS_DATA, {
                name: 'holidays'
            }, function (err, data) {
                if (err) {
                    this.appendFlashMessage({
                        level: 'alert-danger',
                        message: HOLIDAYS_DATA_GET_ERROR_MESSAGE,
                    }, this.$root);
                    return;
                }
                this.holidays = data;
            }.bind(this));
        },

        /**
         * カレンダーを取得する
         */
        getCalendarElement: function () {
            return (this.$refs?.calendarParent || this.$nextTick(() => this.getCalendarElement()));
        },

        // -------------------------
        // emit
        // -------------------------

        // カレンダー設定を更新したら
        emitUpdateValue: function() {
            this.$emit('update:modelValue', this.config_days);
        }
    }
};
